import { useMemo } from "react";
import { GlassPanel } from "@/components/ui/glass-panel";
import { Stars } from "lucide-react";
import { cn } from "@/lib/utils";
import { scoreStargazing } from "@/lib/insights/stargazing";

interface StargazingCardProps {
  daily?: {
    clouds?: number;
    humidity?: number;
    moon_phase?: number;
    sunset?: number;
    moonrise?: number;
    moonset?: number;
  }[];
  hourly?: { dt: number; clouds?: number; humidity?: number }[];
  current?: { sunset?: number };
}

function ratingColor(r: string) {
  switch (r) {
    case "Excellent":
      return { text: "text-emerald-400", ring: "ring-emerald-500/30", bg: "bg-emerald-500/10" };
    case "Good":
      return { text: "text-lime-400", ring: "ring-lime-500/30", bg: "bg-lime-500/10" };
    case "Fair":
      return { text: "text-amber-400", ring: "ring-amber-500/30", bg: "bg-amber-500/10" };
    default:
      return { text: "text-slate-400", ring: "ring-slate-500/30", bg: "bg-slate-500/10" };
  }
}

export function StargazingCard({ daily, hourly, current }: StargazingCardProps) {
  const today = daily?.[0];

  // Prefer the hourly entry around 22:00 local (3-4 hours after sunset) for cloud/humidity at night.
  const sunset = today?.sunset ?? current?.sunset;
  const targetTs = sunset ? sunset + 3 * 3600 : null;
  const nightHour = useMemo(() => {
    if (!hourly || !targetTs) return null;
    let best = hourly[0];
    let bestDelta = Math.abs(best.dt - targetTs);
    for (const h of hourly) {
      const d = Math.abs(h.dt - targetTs);
      if (d < bestDelta) {
        bestDelta = d;
        best = h;
      }
    }
    return best;
  }, [hourly, targetTs]);

  if (!today || today.moon_phase == null) return null;

  const clouds = nightHour?.clouds ?? today.clouds ?? 50;
  const humidity = nightHour?.humidity ?? today.humidity ?? 60;

  const result = scoreStargazing({
    cloudPct: clouds,
    humidity,
    moonPhase: today.moon_phase,
    sunset,
    moonrise: today.moonrise,
    moonset: today.moonset,
  });

  const c = ratingColor(result.rating);
  const pct = result.score * 10;

  return (
    <GlassPanel intensity="low" className="p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-500/10 ring-1 ring-indigo-500/20">
            <Stars className="h-4 w-4 text-indigo-300" />
          </div>
          <div>
            <h3 className="text-sm font-bold tracking-tight">Stargazing Tonight</h3>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Cloud · humidity · moon
            </p>
          </div>
        </div>
        <div
          className={cn(
            "flex items-center justify-center h-12 w-12 rounded-xl ring-1 flex-shrink-0",
            c.ring,
            c.bg,
          )}
        >
          <span className={cn("text-base font-bold tabular-nums", c.text)}>
            {result.score.toFixed(1)}
          </span>
        </div>
      </div>

      <div className="h-1.5 rounded-full bg-border/40 overflow-hidden mb-3">
        <div
          className={cn(
            "h-full rounded-full",
            result.rating === "Excellent"
              ? "bg-emerald-400"
              : result.rating === "Good"
                ? "bg-lime-400"
                : result.rating === "Fair"
                  ? "bg-amber-400"
                  : "bg-slate-400",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-background/40 border border-border/40 p-2">
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Cloud</p>
          <p className="text-sm font-bold tabular-nums">{Math.round(clouds)}%</p>
        </div>
        <div className="rounded-lg bg-background/40 border border-border/40 p-2">
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Humidity</p>
          <p className="text-sm font-bold tabular-nums">{Math.round(humidity)}%</p>
        </div>
        <div className="rounded-lg bg-background/40 border border-border/40 p-2">
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Moon</p>
          <p className="text-sm font-bold tabular-nums">
            {Math.round(result.moonIllumination * 100)}%
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <span className={cn("font-semibold", c.text)}>{result.rating}</span>
        <span className="text-muted-foreground">·</span>
        <span className="text-foreground/80">{result.moonLabel}</span>
      </div>

      {result.reason && (
        <p className="mt-1 text-[11px] text-muted-foreground capitalize">{result.reason}</p>
      )}

      {result.bestWindow && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          <span className="font-semibold text-foreground/80">Best window:</span>{" "}
          {result.bestWindow.label}
        </p>
      )}
    </GlassPanel>
  );
}
